import { EarningsPeriod } from '@api';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import {
  DropdownEntry,
  DropdownGroup,
} from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import {
  type AppTableFeatures,
  TableHeader,
  TableHeaderIcons,
} from '@design/components/table';
import { flexRender, Header } from '@tanstack/react-table';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import clsx from 'clsx';
import { Property } from 'csstype';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../Earnings.module.scss';
import { useEarningsStore } from '../useEarningsStore/useEarningsStore';
import {
  EARNINGS_COLUMN_ID,
  PERIOD_COLUMN_ID,
  RELEASED_ON_COLUMN_ID,
} from '../utils/utils';

const DATE_HEADERS_IDS = [RELEASED_ON_COLUMN_ID];

export const EarningsTableHeaderCell = ({
  header,
  isLastHeaderGroup,
  isPinnedVertically,
  isHighlighted,
}: {
  header: Header<AppTableFeatures, EarningsPeriod, unknown>;
  isLastHeaderGroup: boolean;
  isPinnedVertically: boolean;
  isHighlighted: boolean;
}) => {
  const { t } = useTranslation('widget');
  const { filterGroups, setActiveSortingState, setActiveModalFilter } =
    useEarningsStore();
  const columnId = header.column.id;
  const filter = filterGroups.EARNINGS_FILTERS.allFilters.find(
    filter => filter.key === columnId
  );
  const isColumnFiltered =
    !!filter &&
    filterGroups.EARNINGS_FILTERS.activeFilterOptions.some(
      option => option.filter === columnId
    );

  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );

  const isDateHeader = DATE_HEADERS_IDS.includes(header.column.id);
  const sortOptions: DropdownEntry[] = [
    {
      label: t('widget:earnings:table_ascending'),
      iconName: 'SortUp',
      key: 'sortUp',
      onClick: () =>
        setActiveSortingState([
          {
            id: header.column.id,
            desc: false,
          },
        ]),
    },
    {
      label: t('widget:earnings:table_descending'),
      iconName: 'SortDown',
      key: 'sortDown',
      onClick: () =>
        setActiveSortingState([
          {
            id: header.column.id,
            desc: true,
          },
        ]),
    },
  ];
  const sortDateOptions: DropdownEntry[] = [
    {
      label: t('widget:earnings:newestFirst'),
      iconName: 'SortDown',
      key: 'SortDown',
      onClick: () =>
        setActiveSortingState([
          {
            id: header.column.id,
            desc: true,
          },
        ]),
    },
    {
      label: t('widget:earnings:oldestFirst'),
      iconName: 'SortUp',
      key: 'SortUp',
      onClick: () =>
        setActiveSortingState([
          {
            id: header.column.id,
            desc: false,
          },
        ]),
    },
  ];

  const filterOption: DropdownEntry[] = [
    {
      label: t('widget:earnings:filter'),
      iconName: 'Filter',
      key: 'filter',
      onClick: () => {
        if (filter) {
          setActiveModalFilter(filter);
        }
      },
    },
  ];

  const sortOrder = header.column.getIsSorted();

  const isFirstTableHeader = header.index === 0;
  const isLastGroup = isLastHeaderGroup;

  const alignment: Property.TextAlign = isLastGroup
    ? [EARNINGS_COLUMN_ID, RELEASED_ON_COLUMN_ID, PERIOD_COLUMN_ID].includes(
        header.column.id
      )
      ? 'left'
      : 'right'
    : isFirstTableHeader
      ? 'left'
      : 'center';

  const tableHeader = (
    <TableHeader
      key={header.id}
      colSpan={header.colSpan}
      alignment={alignment}
      className={clsx(styles.styledTableHeader, {
        [styles.headerHighlighted]: isHighlighted,
        [styles.headerPinned]: isLastGroup && isPinnedVertically,
        [styles.headerLastPinned]:
          isLastGroup &&
          isPinnedVertically &&
          header.column.getIsLastColumn('start'),
        [styles.headerSortable]: isLastGroup && header.column.getCanSort(),
        [styles.headerLastGroup]: isLastGroup,
        [styles.headerGroupRow]: !isLastGroup,
      })}
      style={
        {
          '--header-left':
            isLastGroup && isPinnedVertically
              ? `${header.column.getStart('start')}px`
              : undefined,
          '--header-right':
            isLastGroup && isPinnedVertically
              ? `${header.column.getAfter('end')}px`
              : undefined,
        } as CSSProperties
      }
    >
      <TableHeaderIcons
        isFiltered={!header.isPlaceholder && isColumnFiltered}
        sortDirection={!header.isPlaceholder && sortOrder}
      >
        {!header.isPlaceholder &&
          flexRender(header.column.columnDef.header, header.getContext())}
      </TableHeaderIcons>
    </TableHeader>
  );

  if (!header.isPlaceholder && header.column.getCanSort()) {
    return (
      <DropdownMenu key={header.id} triggerNode={tableHeader}>
        <DropdownGroup items={isDateHeader ? sortDateOptions : sortOptions} />
        {filter && !isNewEarningsTabEnabled && (
          <DropdownGroup items={filterOption} />
        )}
      </DropdownMenu>
    );
  }

  return tableHeader;
};
